import type { MutableRefObject } from 'react'
import { ClikArtPlacementCanvasBridge } from '@/components/app/clikart/clikart-placement-canvas-bridge'
import { ColorPlacementCanvasBridge } from '@/components/app/editor/color-placement-canvas-bridge'
import { PhotoPlacementCanvasBridge } from '@/components/app/photos/photo-placement-canvas-bridge'
import { TemplateDropOverlay } from '@/components/app/templates/template-drop-overlay'
import { TemplateDropWarning } from '@/components/app/templates/template-drop-warning'
import { TemplatePlacementCanvasBridge } from '@/components/app/templates/template-placement-canvas-bridge'
import {
  type SpreadBackgroundGeometry,
  SpreadBackgroundLayer,
} from '@/components/app/backgrounds/spread-background-layer'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'
import { isClientPointOverPhotoDropSurface } from '@/lib/photos/photo-placement-hit-test'
import type { CenterCanvasConfig } from '../types/center-canvas-config'

/** Whether a client point is over the pasteboard (the tray drop surface). */
function isClientPointOverPasteboard(clientX: number, clientY: number) {
  return isClientPointOverPhotoDropSurface(clientX, clientY)
}

/**
 * The document's tray drop targets: the photo, ClikArt and template placement bridges, the
 * template drag-over tint and its over-existing-content warning, and the spread background drop
 * layer, all mapping client points into canvas space the same way.
 */
export function CanvasDropTargets({
  getCanvasPointFromPointerEvent,
  bleedWidth,
  bleedHeight,
  internalShapeManagerRef,
  linkedTextChains,
  spreadBackgroundGeometry,
}: {
  getCanvasPointFromPointerEvent: (
    clientX: number,
    clientY: number
  ) => { canvasX: number; canvasY: number } | null
  bleedWidth: number
  bleedHeight: number
  internalShapeManagerRef: MutableRefObject<ShapeManagerRef | null>
  linkedTextChains: CenterCanvasConfig['linkedTextChains']
  spreadBackgroundGeometry: SpreadBackgroundGeometry
}) {
  const toCanvasPoint = (clientX: number, clientY: number) => {
    const pt = getCanvasPointFromPointerEvent(clientX, clientY)

    return pt ? { canvasX: pt.canvasX, canvasY: pt.canvasY } : null
  }

  return (
    <>
      <ColorPlacementCanvasBridge
        toCanvasPoint={toCanvasPoint}
        isOverCanvas={isClientPointOverPasteboard}
        bleedWidth={bleedWidth}
        bleedHeight={bleedHeight}
        shapeManagerRef={internalShapeManagerRef}
      />
      <PhotoPlacementCanvasBridge
        toCanvasPoint={toCanvasPoint}
        isOverCanvas={isClientPointOverPasteboard}
        bleedWidth={bleedWidth}
        bleedHeight={bleedHeight}
        shapeManagerRef={internalShapeManagerRef}
        linkedTextChains={linkedTextChains}
      />
      <ClikArtPlacementCanvasBridge
        toCanvasPoint={toCanvasPoint}
        isOverCanvas={isClientPointOverPasteboard}
        bleedWidth={bleedWidth}
        bleedHeight={bleedHeight}
        shapeManagerRef={internalShapeManagerRef}
        linkedTextChains={linkedTextChains}
      />
      <TemplatePlacementCanvasBridge
        toCanvasPoint={toCanvasPoint}
        isOverCanvas={isClientPointOverPasteboard}
        bleedWidth={bleedWidth}
        bleedHeight={bleedHeight}
        shapeManagerRef={internalShapeManagerRef}
      />
      <TemplateDropOverlay />
      <TemplateDropWarning shapeManagerRef={internalShapeManagerRef} />
      <SpreadBackgroundLayer
        toCanvasPoint={toCanvasPoint}
        isOverCanvas={isClientPointOverPasteboard}
        {...spreadBackgroundGeometry}
      />
    </>
  )
}
